import i18next, { type i18n } from "i18next";
import { initReactI18next } from "react-i18next";

import { DEFAULT_NAMESPACE, DOCS_NAMESPACE, NAMESPACES, resources } from "./resources";

/**
 * @mountayaapp/locales is the render-free i18next setup + locale files, shared
 * by web and docs. The en/fr key-parity gate is active (parity.test.ts) and
 * key-level type safety is wired via i18next's CustomTypeOptions augmentation
 * (i18next.d.ts), so t() keys are compile-checked across both apps.
 */

/**
 * createI18n builds an i18next instance bound to react-i18next. The active
 * language is injected by the app shell (SPA bootstrap / embed search param)
 * rather than detected here, keeping the package environment-agnostic.
 */
export const createI18n = (language: string = "en"): i18n => {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    defaultNS: DEFAULT_NAMESPACE,
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    lng: language,
    ns: NAMESPACES,
    resources,
  });

  return instance;
};

/**
 * createDocsI18n builds an i18next instance for the developer-documentation app,
 * scoped to the namespaces docs needs: the shared common namespace plus the
 * English-only docs namespace. It mirrors createI18n but loads only those two
 * bundles, keeping the docs chrome out of the parity-gated web namespaces. The
 * active language is injected by the docs shell (resolved from the URL) rather
 * than detected here, so the package stays environment-agnostic; only English
 * ships today, and a second language becomes available the moment its bundles
 * exist in resources.
 */
export const createDocsI18n = (language: string = "en"): i18n => {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    defaultNS: DEFAULT_NAMESPACE,
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    lng: language,
    ns: [DEFAULT_NAMESPACE, DOCS_NAMESPACE],
    resources,
  });

  return instance;
};

export type { SupportedLanguage } from "./resources";
export {
  DEFAULT_LANGUAGE,
  DEFAULT_NAMESPACE,
  DOCS_NAMESPACE,
  NAMESPACES,
  resources,
  SUPPORTED_LANGUAGES,
} from "./resources";
